import { useState, useRef, useEffect, useId } from "react";
import Collapsible from "react-collapsible";
import { Sanity } from "@models/sanity.model";
import { StyledDetailAccordion, StyledContent, StyledTextFallback } from "./DetailAccordion.styled";
import PortableText from "@components/portable-text";

interface DetailAccordionProps {
  id: string;
  label: string;
  content: Sanity.PortableText;
}

const DetailAccordion: React.FC<DetailAccordionProps> = ({ id, label, content }) => {
  const [accordionOpen, setAccordionOpen] = useState<boolean>(false);
  const accordionRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLElement | undefined>();
  const genId = useId();

  const accordionId = `accordion-footer-${id}`;
  const footerId = `footer-${id}`;

  // Have to add a11y attributes to accordion content manually
  // since plugin doesn't give access to it

  useEffect(() => {
    if (!accordionRef.current) return;

    contentRef.current = accordionRef.current.querySelector(".Collapsible__contentOuter");

    if (contentRef.current) {
      contentRef.current.id = footerId;
      contentRef.current.dataset.ariaLabelledBy = accordionId;
      contentRef.current.dataset.role = "region";
    }
  }, []);

  return (
    <StyledDetailAccordion ref={accordionRef}>
      {label && content ? (
        <Collapsible
          contentElementId={`collapsible-content-${genId}`}
          trigger={label}
          triggerTagName="button"
          triggerElementProps={{
            id: accordionId,
            "aria-controls": footerId,
            "aria-expanded": accordionOpen,
          }}
          transitionTime={200}
          easing={"ease-out"}
          onOpening={() => {
            setAccordionOpen(true);
          }}
          onClosing={() => {
            setAccordionOpen(false);
          }}>
          <StyledContent>
            <PortableText value={content} />
          </StyledContent>
        </Collapsible>
      ) : label ? (
        <StyledTextFallback>
          <span>{label}</span>
        </StyledTextFallback>
      ) : null}
    </StyledDetailAccordion>
  );
};

export default DetailAccordion;
